import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Avatar,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { updateUserProfile, getUserProfile } from '../../services/userProfileService';
import './EditProfile.css';

const SOCIAL_PLATFORMS = {
  instagram: {
    label: 'Instagram',
    placeholder: 'https://instagram.com/username',
    impp: 'instagram',
    urlToImpp: (url) => {
      const username = url.split('instagram.com/').pop()?.split('/')[0];
      return username ? `instagram:${username}` : url;
    }
  },
  facebook: {
    label: 'Facebook',
    placeholder: 'https://facebook.com/username',
    impp: 'facebook',
    urlToImpp: (url) => {
      const username = url.split('facebook.com/').pop()?.split('/')[0];
      return username ? `facebook:${username}` : url;
    }
  },
  linkedin: {
    label: 'LinkedIn',
    placeholder: 'https://linkedin.com/in/username',
    impp: 'linkedin',
    urlToImpp: (url) => {
      const username = url.split('linkedin.com/in/').pop()?.split('/')[0];
      return username ? `linkedin:${username}` : url;
    }
  },
  skype: {
    label: 'Skype',
    placeholder: 'skype:username',
    impp: 'skype',
    urlToImpp: (url) => url.replace('skype:', '')
  },
  telegram: {
    label: 'Telegram',
    placeholder: '@username or username',
    impp: 'telegram',
    urlToImpp: (url) => url.replace('@', '')
  },
  whatsapp: {
    label: 'WhatsApp',
    placeholder: 'Phone number with country code (e.g., +1234567890)',
    impp: 'whatsapp',
    urlToImpp: (url) => url.replace(/[^0-9+]/g, ''),
    formatUrl: (input) => {
      // Clean the phone number to only keep digits and plus sign
      const phone = input.replace(/[^0-9+]/g, '');
      return `https://wa.me/${phone.replace('+', '')}`;
    }
  },
  website: {
    label: 'Website',
    placeholder: 'https://www.example.com'
  }
};

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

function EditProfile() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    company: '',
    companyWebsite: '',
    photoURL: '',
    socialLinks: [],
    phonePrivacy: 'public',
    emailPrivacy: 'public'
  });
  const [newSocialLink, setNewSocialLink] = useState({ platform: '', url: '' });
  const [editingSocialLink, setEditingSocialLink] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const userProfile = await getUserProfile(currentUser.uid);
        if (userProfile) {
          setFormData({
            name: userProfile.name || currentUser.displayName || '',
            email: userProfile.email || currentUser.email || '',
            phone: userProfile.phone || '',
            role: userProfile.role || '',
            company: userProfile.company || '',
            companyWebsite: userProfile.companyWebsite || '',
            photoURL: userProfile.photoURL || currentUser.photoURL || '',
            socialLinks: userProfile.socialLinks || [],
            phonePrivacy: userProfile.phonePrivacy || 'public',
            emailPrivacy: userProfile.emailPrivacy || 'public'
          });
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
        setError('Failed to load profile');
      }
    };

    if (currentUser) {
      fetchProfile();
    }
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        alert('File is too large. Maximum size is 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({
          ...prev,
          photoURL: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const profileData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        role: formData.role,
        company: formData.company,
        companyWebsite: formData.companyWebsite,
        photoURL: formData.photoURL,
        socialLinks: formData.socialLinks,
        phonePrivacy: formData.phonePrivacy,
        emailPrivacy: formData.emailPrivacy
      };

      await updateUserProfile(currentUser.uid, profileData);
      navigate(`/profile/${currentUser.uid}`);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSocialLink = () => {
    setDialogOpen(true);
  };

  const handleEditSocialLink = (link, index) => {
    setEditingSocialLink({ ...link, index });
    setDialogOpen(true);
  };

  const handleSocialLinkSubmit = () => {
    if (editingSocialLink) {
      // Update existing link
      const platform = SOCIAL_PLATFORMS[editingSocialLink.platform];
      const formattedUrl = platform?.formatUrl ? 
        platform.formatUrl(editingSocialLink.url) : 
        editingSocialLink.url;

      setFormData(prev => ({
        ...prev,
        socialLinks: prev.socialLinks.map((link, i) => 
          i === editingSocialLink.index ? 
          { platform: editingSocialLink.platform, url: formattedUrl } : 
          link
        )
      }));
    } else if (editingSocialLink === null && newSocialLink.platform && newSocialLink.url) {
      // Add new link
      const platform = SOCIAL_PLATFORMS[newSocialLink.platform];
      const formattedUrl = platform?.formatUrl ? 
        platform.formatUrl(newSocialLink.url) : 
        newSocialLink.url;

      setFormData(prev => ({
        ...prev,
        socialLinks: [...prev.socialLinks, { 
          platform: newSocialLink.platform, 
          url: formattedUrl 
        }]
      }));
    }
    handleCloseDialog();
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingSocialLink(null);
    setNewSocialLink({ platform: '', url: '' });
  };

  const handleDeleteSocialLink = (index) => {
    setFormData(prev => ({
      ...prev,
      socialLinks: prev.socialLinks.filter((_, i) => i !== index)
    }));
  };

  const handlePrivacyChange = async (e) => {
    const { name, checked } = e.target;
    const newValue = checked ? 'private' : 'public';
    
    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    try {
      // Save privacy setting immediately
      await updateUserProfile(currentUser.uid, {
        [name]: newValue
      });
    } catch (error) {
      console.error('Error updating privacy setting:', error);
      // Revert the change if save fails
      setFormData(prev => ({
        ...prev,
        [name]: prev[name]
      }));
    }
  };

  return (
    <Container maxWidth="sm" className="edit-profile-container">
      <Paper elevation={0} className="edit-profile-paper">
        <Typography variant="h5" gutterBottom align="center">
          Edit Profile
        </Typography>

        {error && (
          <Typography color="error" align="center" gutterBottom>
            {error}
          </Typography>
        )}

        <Box className="avatar-section">
          <Avatar
            src={formData.photoURL || '/default-avatar.png'}
            alt={formData.name}
            className="profile-avatar"
          />
          <input
            type="file"
            accept="image/*"
            id="profile-picture-input"
            style={{ display: 'none' }}
            onChange={handlePhotoChange}
          />
          <label htmlFor="profile-picture-input">
            <IconButton
              component="span"
              className="upload-button"
              size="small"
              aria-label="upload picture"
            >
              <PhotoCameraIcon />
            </IconButton>
          </label>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            margin="normal"
          />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  sx={{ flexGrow: 1, mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.emailPrivacy === 'private'}
                      onChange={handlePrivacyChange}
                      name="emailPrivacy"
                      color="primary"
                    />
                  }
                  label={formData.emailPrivacy === 'private' ? 'Private' : 'Public'}
                  labelPlacement="start"
                  sx={{ minWidth: 100 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  sx={{ flexGrow: 1, mr: 2 }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.phonePrivacy === 'private'}
                      onChange={handlePrivacyChange}
                      name="phonePrivacy"
                      color="primary"
                    />
                  }
                  label={formData.phonePrivacy === 'private' ? 'Private' : 'Public'}
                  labelPlacement="start"
                  sx={{ minWidth: 100 }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Box className="form-section">
          <Typography variant="h6" gutterBottom>
            Company Information
          </Typography>
          <TextField
            fullWidth
            label="Company Name"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Company Website"
            name="companyWebsite"
            value={formData.companyWebsite}
            onChange={handleInputChange}
            margin="normal"
            placeholder="https://www.example.com"
          />
          <TextField
            fullWidth
            label="Role"
            name="role"
            value={formData.role}
            onChange={handleInputChange}
            margin="normal"
          />
        </Box>

        <Box className="form-section">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Social Links
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddSocialLink}
              variant="outlined"
              size="small"
            >
              Add Link
            </Button>
          </Box>
          <List>
            {formData.socialLinks.map((link, index) => (
              <ListItem 
                key={index}
                secondaryAction={
                  <Box>
                    <IconButton
                      edge="end"
                      onClick={() => handleEditSocialLink(link, index)}
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      edge="end"
                      onClick={() => handleDeleteSocialLink(index)}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                }
              >
                <ListItemText
                  primary={link.platform.charAt(0).toUpperCase() + link.platform.slice(1)}
                  secondary={link.url}
                />
              </ListItem>
            ))}
          </List>
        </Box>

        <Box className="action-buttons">
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
            fullWidth
          >
            Save Profile
          </Button>
        </Box>

        <Dialog open={dialogOpen} onClose={handleCloseDialog}>
          <DialogTitle>
            {editingSocialLink ? 'Edit Social Link' : 'Add Social Link'}
          </DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Platform</InputLabel>
              <Select
                value={editingSocialLink ? editingSocialLink.platform : newSocialLink.platform}
                onChange={(e) => {
                  if (editingSocialLink) {
                    setEditingSocialLink(prev => ({ ...prev, platform: e.target.value }));
                  } else {
                    setNewSocialLink(prev => ({ ...prev, platform: e.target.value }));
                  }
                }}
                label="Platform"
              >
                {Object.entries(SOCIAL_PLATFORMS).map(([key, platform]) => (
                  <MenuItem key={key} value={key}>
                    {platform.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="URL or Username"
              value={editingSocialLink ? editingSocialLink.url : newSocialLink.url}
              onChange={(e) => {
                if (editingSocialLink) {
                  setEditingSocialLink(prev => ({ ...prev, url: e.target.value }));
                } else {
                  setNewSocialLink(prev => ({ ...prev, url: e.target.value }));
                }
              }}
              margin="normal"
              placeholder={
                editingSocialLink 
                  ? SOCIAL_PLATFORMS[editingSocialLink.platform]?.placeholder 
                  : newSocialLink.platform 
                    ? SOCIAL_PLATFORMS[newSocialLink.platform]?.placeholder 
                    : 'Enter URL or username'
              }
              helperText={
                editingSocialLink?.platform || newSocialLink.platform
                  ? SOCIAL_PLATFORMS[editingSocialLink?.platform || newSocialLink.platform]?.placeholder
                  : 'Enter the full URL or username for the selected platform'
              }
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              onClick={handleSocialLinkSubmit} 
              color="primary"
              disabled={
                editingSocialLink ? 
                !editingSocialLink.platform || !editingSocialLink.url :
                !newSocialLink.platform || !newSocialLink.url
              }
            >
              {editingSocialLink ? 'Save' : 'Add'}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </Container>
  );
}

export default EditProfile;
